/* eslint-disable camelcase, complexity, require-jsdoc */
import aucklandStadiumsLogo from 'static/images/logos/auckland-stadiums-logo-square.png';
import { getStadiumByName, getStadiumBySlug } from 'config/stadiums-structured-data';
import getPageParentBySlug from 'app/utilities/get-page-parent-by-slug';
import { isPastEvent } from 'app/utilities/date-time';

export const SITE_ORIGIN = 'https://www.aucklandstadiums.co.nz';
export const SITE_URL = `${SITE_ORIGIN}/`;
export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

export const PARENT_ORGANIZATION = {
    '@type': 'Organization',
    '@id': 'https://www.aucklandunlimited.com/#organization',
    name: 'Tātaki Auckland Unlimited',
    alternateName: ['Auckland Unlimited', 'TAU'],
    url: 'https://www.aucklandunlimited.com/',
    logo: {
        '@type': 'ImageObject',
        url: 'https://www.aucklandunlimited.com/static/assets/images/tataki-auckland-unlimited-a-navy-rgb.26dbb9e1deac145cabbb.svg'
    },
    sameAs: [
        'https://www.linkedin.com/company/auckland-unlimited',
        'https://www.wikidata.org/wiki/Q55764471'
    ]
};

export function absoluteUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return undefined;

    const candidate = value.charAt(0) === '/' ? `${SITE_ORIGIN}${value}` : value;

    try {
        const parsed = new URL(candidate);

        return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : undefined;
    } catch (error) {
        return undefined;
    }
}

function buildEntityPath(type, attributes) {
    const { id, parent, slug } = attributes;

    switch (type) {
        case 'event':
        case 'events':
            return slug ? `/event/${slug}` : undefined;
        case 'news':
            return slug ? `/news/${slug}` : undefined;
        case 'performance':
        case 'performances':
            return id ? `/performance/${id}` : undefined;
        case 'venue':
        case 'venues':
            return slug ? `/our-venues/${slug}` : undefined;
        default:
            return slug ? parent ? `/${parent}/${slug}` : `/${slug}` : undefined;
    }
}

export function buildCanonicalUrl(page) {
    if (!page || !page.attributes) return undefined;

    const { attributes, id, type } = page;
    const { slug } = attributes;

    if (slug === 'home') return SITE_URL;

    return absoluteUrl(buildEntityPath(type, { ...attributes, id }));
}

function plainText(value) {
    if (typeof value !== 'string') return value;

    return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

function imageUrls(value) {
    const values = Array.isArray(value) ? value : [value];

    return values.map(absoluteUrl).filter(Boolean);
}

function getPageImage(attributes) {
    const heroImages = attributes.hero_image &&
        attributes.hero_image.images &&
        attributes.hero_image.images.desktop;
    const images = imageUrls(heroImages || attributes.main_images || attributes.thumbnail);

    return images.length === 1 ? images[0] : images;
}

function getDescription(attributes) {
    return plainText(
        attributes.seo_description ||
        attributes.description
    );
}

export function buildOrganizationSchema() {
    return {
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: 'Auckland Stadiums',
        url: SITE_URL,
        logo: {
            '@type': 'ImageObject',
            '@id': `${SITE_ORIGIN}/#logo`,
            url: absoluteUrl(aucklandStadiumsLogo)
        },
        parentOrganization: { '@id': PARENT_ORGANIZATION['@id'] },
        sameAs: [
            'https://www.linkedin.com/company/auckland-stadiums/'
        ]
    };
}

export function buildWebSiteSchema() {
    return {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: SITE_URL,
        name: 'Auckland Stadiums',
        publisher: { '@id': ORGANIZATION_ID },
        inLanguage: 'en-NZ'
    };
}

function getWebPageType(page) {
    const { slug } = page.attributes;

    if (slug === 'about-us') return 'AboutPage';
    if (slug === 'contact-us' && page.attributes.parent === 'about-us') return 'ContactPage';
    if (['news', 'our-venues', 'whats-on'].includes(slug) && page.type === 'pages') return 'CollectionPage';

    return 'WebPage';
}

export function buildWebPageSchema(page, canonicalUrl, hasBreadcrumb = true) {
    const { attributes } = page;
    const webPageId = `${canonicalUrl}#webpage`;
    const schema = {
        '@type': getWebPageType(page),
        '@id': webPageId,
        url: canonicalUrl,
        name: attributes.seo_title || attributes.name,
        description: getDescription(attributes),
        breadcrumb: hasBreadcrumb ? { '@id': `${canonicalUrl}#breadcrumb` } : undefined,
        inLanguage: 'en-NZ'
    };

    if (attributes.slug === 'about-us' || attributes.slug === 'contact-us') {
        schema.about = { '@id': ORGANIZATION_ID };
    }

    return schema;
}

function breadcrumbTail(page, canonicalUrl) {
    const { attributes, type } = page;
    const tail = [];
    let parent;

    if (type === 'events') parent = { name: 'What\'s on', url: `${SITE_ORIGIN}/whats-on` };
    if (type === 'news') parent = { name: 'News', url: `${SITE_ORIGIN}/news` };
    if (type === 'venues') parent = { name: 'Our venues', url: `${SITE_ORIGIN}/our-venues` };
    if (attributes.parent) {
        const parentPage = getPageParentBySlug(attributes.parent);

        if (parentPage) parent = { name: parentPage.name, url: `${SITE_ORIGIN}/${parentPage.slug}` };
    }

    if (parent && parent.url !== canonicalUrl) tail.push(parent);
    tail.push({ name: attributes.name, url: canonicalUrl });

    return tail;
}

export function buildBreadcrumbSchema(page, canonicalUrl = buildCanonicalUrl(page)) {
    if (!canonicalUrl || page.attributes.slug === 'home') return undefined;

    const crumbs = [
        { name: 'Auckland Stadiums', url: SITE_URL },
        ...breadcrumbTail(page, canonicalUrl)
    ];

    return {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: crumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.name,
            item: crumb.url
        }))
    };
}

function faqQuestionsFromBlocks(blocks) {
    return (Array.isArray(blocks) ? blocks : [])
        .filter((block) => block && block.type === 'faq')
        .reduce((questions, block) => questions.concat(
            (Array.isArray(block.data) ? block.data : [])
                .filter((item) => item && item.title && item.content)
                .map((item) => ({
                    '@type': 'Question',
                    name: plainText(item.title),
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: plainText(item.content)
                    }
                }))
        ), []);
}

function pageFaqBlocks(attributes) {
    return []
        .concat(attributes.visitor_blocks || [])
        .concat(attributes.blocks || [])
        .concat(attributes.hire_venue_blocks || []);
}

export function buildFaqSchema(attributes, canonicalUrl, isVenue) {
    const questions = faqQuestionsFromBlocks(pageFaqBlocks(attributes));

    if (!questions.length) return undefined;

    return {
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        url: canonicalUrl,
        name: isVenue ? `${attributes.name} FAQs` : attributes.name,
        about: isVenue ? { '@id': `${canonicalUrl}#venue` } : undefined,
        mainEntity: questions,
        inLanguage: 'en-NZ'
    };
}

function buildNamedEntity(value, defaultType) {
    if (!value) return undefined;
    if (typeof value === 'string') return { '@type': defaultType, name: value };

    return {
        '@type': value.type || value['@type'] || defaultType,
        name: value.name,
        url: absoluteUrl(value.url)
    };
}

function stadiumAddress(stadium) {
    return {
        '@type': 'PostalAddress',
        addressCountry: 'New Zealand',
        addressLocality: stadium.addressLocality,
        addressRegion: 'Auckland',
        postalCode: stadium.postalCode,
        streetAddress: stadium.streetAddress
    };
}

function stadiumGeo(stadium) {
    return {
        '@type': 'GeoCoordinates',
        latitude: stadium.latitude,
        longitude: stadium.longitude
    };
}

function buildVenueSchema(attributes, canonicalUrl) {
    const stadium = getStadiumBySlug(attributes.slug) || getStadiumByName(attributes.name);

    return {
        '@type': 'StadiumOrArena',
        '@id': `${canonicalUrl}#venue`,
        name: attributes.name,
        url: canonicalUrl,
        image: getPageImage(attributes),
        address: stadium && stadiumAddress(stadium),
        geo: stadium && stadiumGeo(stadium),
        telephone: stadium && stadium.telephone
    };
}

function buildEventLocation(attributes) {
    const venueName = attributes.venues;
    if (!venueName) return undefined;

    const stadium = getStadiumByName(venueName);
    if (!stadium) return { '@type': 'StadiumOrArena', name: venueName };

    const venueUrl = `${SITE_ORIGIN}/our-venues/${stadium.slug}`;

    return {
        '@type': 'StadiumOrArena',
        '@id': `${venueUrl}#venue`,
        name: stadium.name,
        url: venueUrl,
        address: stadiumAddress(stadium),
        geo: stadiumGeo(stadium)
    };
}

function buildOffer(attributes) {
    const ticketUrl = absoluteUrl(attributes.buy_ticket_link);
    if (!ticketUrl) return undefined;

    return {
        '@type': 'Offer',
        url: ticketUrl,
    };
}

export function buildEventSchema(attributes, canonicalUrl) {
    if (!attributes.name || isPastEvent(attributes.end_date || attributes.start_date)) {
        return undefined;
    }

    return {
        '@type': 'Event',
        '@id': `${canonicalUrl}#event`,
        name: attributes.name,
        url: canonicalUrl,
        description: getDescription(attributes),
        image: getPageImage(attributes),
        startDate: attributes.start_date,
        endDate: attributes.end_date,
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: buildEventLocation(attributes),
        offers: buildOffer(attributes)
    };
}

function buildAbout(tags) {
    if (!Array.isArray(tags)) return undefined;

    return tags.map((tag) => buildNamedEntity(tag, 'Thing')).filter(Boolean);
}

export function buildArticleSchema(attributes, canonicalUrl) {
    return {
        '@type': 'NewsArticle',
        '@id': `${canonicalUrl}#article`,
        headline: attributes.name,
        description: getDescription(attributes),
        image: getPageImage(attributes),
        publisher: { '@id': ORGANIZATION_ID },
        about: buildAbout(attributes.tags),
        inLanguage: 'en-NZ'
    };
}

function entityItemUrl(item) {
    if (!item || !item.data) return undefined;

    return absoluteUrl(buildEntityPath(item.type, item.data));
}

function contentSetItems(blocks) {
    if (!Array.isArray(blocks)) return [];

    return blocks.reduce((items, block) => {
        const blockItems = block &&
            block.data &&
            ['contentset', 'crosspromo', 'upcoming-events', 'upcomingevents'].includes(block.type) &&
            block.data.items;

        return Array.isArray(blockItems) ? items.concat(blockItems) : items;
    }, []);
}

export function buildItemListSchema(attributes, canonicalUrl, acceptedType) {
    const seen = {};
    const items = contentSetItems(attributes.blocks)
        .filter((item) => !acceptedType || item.type === acceptedType)
        .filter((item) => {
            if (acceptedType !== 'event') return true;

            const data = item.data;
            if (!data || !data.start_date) return false;

            return !isPastEvent(data.end_date || data.start_date);
        })
        .map((item) => ({
            name: item.data && item.data.name,
            url: entityItemUrl(item)
        }))
        .filter((item) => item.name && item.url)
        .filter((item) => {
            if (seen[item.url]) return false;
            seen[item.url] = true;

            return true;
        });

    if (!items.length) return undefined;

    return {
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#itemlist`,
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url
        }))
    };
}

function listingType(slug) {
    if (slug === 'our-venues') return 'venue';
    if (slug === 'whats-on') return 'event';

    return undefined;
}

export function buildPageGraph(page, canonicalUrl = buildCanonicalUrl(page)) {
    if (!canonicalUrl || !page.attributes) return undefined;

    const { attributes, type } = page;
    const isHome = attributes.slug === 'home';
    const acceptedListingType = listingType(attributes.slug);
    const itemList = type === 'pages' && acceptedListingType ?
        buildItemListSchema(attributes, canonicalUrl, acceptedListingType) :
        undefined;
    const faq = buildFaqSchema(attributes, canonicalUrl, type === 'venues');
    const webPage = buildWebPageSchema(page, canonicalUrl, !isHome);

    const graph = [
        buildOrganizationSchema(),
        PARENT_ORGANIZATION,
        buildWebSiteSchema(),
        webPage,
        buildBreadcrumbSchema(page, canonicalUrl)
    ];

    if (type === 'venues') graph.push(buildVenueSchema(attributes, canonicalUrl));
    if (type === 'events') graph.push(buildEventSchema(attributes, canonicalUrl));
    if (type === 'news') graph.push(buildArticleSchema(attributes, canonicalUrl));
    if (faq) graph.push(faq);
    if (itemList) graph.push(itemList);

    return {
        '@context': 'https://schema.org',
        '@graph': graph.filter(Boolean)
    };
}
